import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { RefreshCw, Trash2, UserPlus, Receipt as RcpIcon, Edit2, WifiOff, CheckCircle2, AlertTriangle, Inbox } from "lucide-react";
import { toast } from "sonner";
import { getQueue, removeFromQueue, updateQueued, retryAll, QUEUE_EVENT } from "../lib/offlineQueue";
import { useOfflineSyncState } from "../lib/OfflineSyncContext";

const fmtIDR = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");

const opMeta = {
  create_customer: { label: "Pelanggan Baru", Icon: UserPlus, color: "#2563eb" },
  create_rental: { label: "Transaksi Baru", Icon: RcpIcon, color: "#DC2626" },
  edit_rental: { label: "Edit Transaksi", Icon: Edit2, color: "#d97706" },
};

function summary(rec) {
  if (rec.op === "create_customer") return rec.payload?.full_name || "-";
  if (rec.op === "edit_rental") return `Order ${rec.rental_id} · ${rec.payload?.items?.length || 0} item`;
  const r = rec.receipt || {};
  return `${r.code || "-"} · ${r.customer_name || "-"} · ${(r.items?.length || 0)} item · ${fmtIDR(r.total)}`;
}

export default function OfflineQueue() {
  const { online, pending, errors, syncing, sync } = useOfflineSyncState() || {};
  const [rows, setRows] = useState([]);

  const load = () => getQueue().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onChange = () => load();
    window.addEventListener(QUEUE_EVENT, onChange);
    return () => window.removeEventListener(QUEUE_EVENT, onChange);
  }, []);

  const removeOne = async (id) => {
    if (!window.confirm("Hapus item ini dari antrian? Data yang belum tersinkron akan hilang permanen.")) return;
    await removeFromQueue(id);
    toast.success("Item dihapus dari antrian");
  };

  const retryOne = async (id) => {
    await updateQueued(id, { status: "pending", error: null });
    if (sync) await sync(false);
  };

  const syncAll = async () => {
    await retryAll();
    if (sync) await sync(false);
  };

  return (
    <div className="space-y-6" data-testid="offline-queue-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Sinkronisasi</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Antrian Offline</h1>
          <p className="text-sm text-neutral-500 mt-1">Transaksi & pelanggan yang dibuat saat offline menunggu dikirim ke server.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 ${online ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`} data-testid="queue-online-status">
            {online ? <><CheckCircle2 className="w-3 h-3" /> Online</> : <><WifiOff className="w-3 h-3" /> Offline</>}
          </span>
          <Button data-testid="queue-sync-all" onClick={syncAll} disabled={!online || syncing || (pending === 0 && errors === 0)} className="text-white disabled:opacity-60" style={{ background: "var(--brand-primary)" }}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Menyinkron..." : "Sinkron Semua"}
          </Button>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="text-sm"><span className="font-mono-tab font-bold text-lg">{pending || 0}</span> <span className="text-neutral-500">menunggu</span></div>
        <div className="text-sm"><span className="font-mono-tab font-bold text-lg text-red-600">{errors || 0}</span> <span className="text-neutral-500">gagal</span></div>
      </div>

      {rows.length === 0 && (
        <Card className="border-neutral-200 shadow-none"><CardContent className="p-10 text-center text-neutral-500" data-testid="queue-empty">
          <Inbox className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
          Tidak ada antrian. Semua data sudah tersinkron.
        </CardContent></Card>
      )}

      <div className="space-y-3">
        {rows.map((rec) => {
          const meta = opMeta[rec.op] || opMeta.create_rental;
          const isError = rec.status === "error";
          return (
            <Card key={rec.local_id} className={`border shadow-none ${isError ? "border-red-200 bg-red-50/40" : "border-neutral-200"}`} data-testid={`queue-item-${rec.local_id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-9 h-9 rounded flex items-center justify-center shrink-0" style={{ background: meta.color + "1a", color: meta.color }}>
                    <meta.Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase font-bold tracking-wider" style={{ color: meta.color }}>{meta.label}</div>
                    <div className="text-sm font-medium text-neutral-900 truncate">{summary(rec)}</div>
                    <div className="text-[11px] text-neutral-400 mt-0.5">{rec.created_at ? new Date(rec.created_at).toLocaleString("id-ID") : ""}</div>
                    {isError && <div className="text-[11px] text-red-600 font-semibold mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {rec.error || "Gagal disinkron"}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isError && <span className="text-[10px] uppercase font-bold text-red-600 bg-red-100 px-2 py-1 rounded">Gagal</span>}
                  {!isError && <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded">Menunggu</span>}
                  <Button size="sm" variant="outline" onClick={() => retryOne(rec.local_id)} disabled={!online || syncing} data-testid={`queue-retry-${rec.local_id}`}><RefreshCw className="w-3 h-3 mr-1" /> Coba lagi</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeOne(rec.local_id)} data-testid={`queue-delete-${rec.local_id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
